import { AiChatEvent } from '@/components/chat/AiChatEvent'
import { ChatAttachments } from '@/components/chat/ChatAttachments'
import { ChatMessageToolbar } from '@/components/chat/ChatMessageToolbar'
import MarkdownContent from '@/components/chat/MarkdownContent'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useTheme } from '@/hooks/useTheme'
import { cn } from '@/lib/utils'
import type { ConversationResponse } from '@/types/response'
import { format } from 'date-fns'
import { RefreshCcw } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

interface AiChatMessageProps {
  message: ConversationResponse
  event?: string
  isOwner?: boolean
  showToolbar?: boolean
  className?: string
  onRetry?: (conversationId: number) => void | Promise<void>
}

export function AiChatMessage({
  message,
  event,
  isOwner = true,
  showToolbar = true,
  className,
  onRetry,
}: AiChatMessageProps) {
  const { chatAssistantProfilePicture } = useTheme()
  const t = useTranslations('chat')
  const [isRetrying, setIsRetrying] = useState(false)
  const attachments = (message.attachments ?? []).filter((attachment) => attachment.role === 'answer')
  const timestamp = message.created ? format(new Date(message.created), 'HH:mm') : ''
  const loadingEvent = message.status === 'success' && !message.reply ? event : undefined

  const handleRetry = async () => {
    if (!onRetry) return

    setIsRetrying(true)
    try {
      await onRetry(message.id)
    } finally {
      setIsRetrying(false)
    }
  }

  return (
    <div className="flex w-full flex-col gap-2">
      <div className="flex items-center gap-2">
        <div className="relative size-8 overflow-clip">
          <img src={chatAssistantProfilePicture} alt="" className="absolute inset-0 size-full" aria-hidden="true" />
        </div>
        <span className="text-ui-title-t3 font-semibold leading-ui-title-t3 text-black">
          {message.agent_name || 'Gen AI'}
        </span>
      </div>

      {loadingEvent ? (
        <AiChatEvent text={loadingEvent} className={className} />
      ) : (
        <div
          className={cn(
            'flex w-full flex-col gap-2 border border-main-border-lv03 rounded-tl-100 rounded-tr-400 rounded-bl-400 rounded-br-400 bg-main-surface-lv-02 pl-4 pr-10 py-3 shadow-elevation-lv03',
            className,
          )}
        >
          {message.reply ? (
            <div className="flex flex-col gap-2">
              <div className="**:text-ui-caption-c1 **:leading-ui-caption-c1 **:text-main-text-lv01">
                <MarkdownContent content={message.reply} />
              </div>
              <ChatAttachments attachments={attachments} />
            </div>
          ) : message.status === 'success' ? (
            <Skeleton className="h-4 w-full" />
          ) : null}
          {message.status !== 'success' && <Alert variant="error">{t('responseError')}</Alert>}
          {message.status === 'error' && isOwner && onRetry && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="w-fit"
              onClick={handleRetry}
              disabled={isRetrying}
            >
              <RefreshCcw className={`size-4 ${isRetrying ? 'animate-spin' : ''}`} />
              {t('retry')}
            </Button>
          )}
          {timestamp && (
            <div className="py-2">
              <span className="text-ui-caption-c2 leading-ui-caption-c2 text-main-text-lv03">{timestamp}</span>
            </div>
          )}
        </div>
      )}

      {showToolbar && message.id >= 0 && (
        <ChatMessageToolbar
          content={message.reply}
          conversationId={message.id}
          feedback={message.feedback}
          versionNo={message.version_no}
          totalVersions={message.total_versions}
          versionRootId={message.version_root_id}
          modelVersionName={message.model_version_name}
          agentId={message.agent_id}
          agentName={message.agent_name}
          isOwner={isOwner}
          isBroadcast={message.type === 'broadcast'}
        />
      )}
    </div>
  )
}
